// src/prototype/components/evaluate/PriorityMatrix.tsx
// ============================================================
// PRIORITY MATRIX VIEW (Evaluate, Oct 21 v3)
// Financial Value (vertical) x Ease of Implementation (horizontal), crosshair at Medium, four
// labeled quadrants: Strategic Investments (top left), Quick Wins (top right), Resource Drains
// (bottom left), Efficiency Boosters (bottom right). Chart on the left, sized to the window;
// clicking a point selects it and a preview shows on the right (VRI order prev/next, add to
// portfolio, open the full use case). Portfolio picks carry a navy ring.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Check, ArrowRight, Clock, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { VerdictPill } from '../VerdictPill';
import { getOpportunityById } from '../../data/mockOpportunities';
import { getDecisionBrief } from '../../data/decisionBrief';
import { hubFigures } from '../../roi/hubFigures';
import { QUADRANT_LABEL, QUADRANT_MIDPOINT, type Quadrant } from '../../data/adapters/stage300FeaturedContent';
import { prioritized, type MatrixPoint } from '../../data/priorityMatrix';

const AXIS = '#3343B0';
const DOT = '#E8676B';
// Compact frame (Oct 21): wide aspect so the whole matrix plus the first list rows fit in one
// view; the chart's rendered height follows the window (see CHART_HEIGHT).
const W = 1000, H = 490;
const X0 = 95, X1 = 975, Y0 = 58, Y1 = 410;
const sx = (v: number) => X0 + (Math.max(0, Math.min(100, v)) / 100) * (X1 - X0);
const sy = (v: number) => Y1 - (Math.max(0, Math.min(100, v)) / 100) * (Y1 - Y0);
/** Space kept below the chart (page padding). */
const RESERVED_BELOW = 24;
const MIN_H = 280, MAX_H = 640;

const Q_STYLE: Record<Quadrant, string> = {
  'Quick Win': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  'Big Bet': 'bg-blue-50 text-blue-800 border-blue-200',
  'Incremental': 'bg-amber-50 text-amber-800 border-amber-200',
  'Deprioritize': 'bg-slate-100 text-slate-600 border-slate-200',
};

const fig = (v: string) => v;

export const QuadrantChip: React.FC<{ q: Quadrant }> = ({ q }) => (
  <span className={`inline-block text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${Q_STYLE[q]}`}>{QUADRANT_LABEL[q]}</span>
);

export interface PriorityMatrixProps {
  points: MatrixPoint[];
  portfolioIds: Set<string>;
  onOpen: (id: string) => void;
  onTogglePortfolio?: (id: string) => void;
  /** Rendered at the start of the legend row (e.g. the scores-as-of chip and Clear filters). */
  headerSlot?: React.ReactNode;
}

export const PriorityMatrix: React.FC<PriorityMatrixProps> = ({ points, portfolioIds, onOpen, onTogglePortfolio, headerSlot }) => {
  const [hover, setHover] = useState<MatrixPoint | null>(null);
  // Fit the chart to the window: the matrix plus the first two list rows in one view.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [chartH, setChartH] = useState(420);
  useEffect(() => {
    const fit = () => {
      const el = svgRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setChartH(Math.max(MIN_H, Math.min(MAX_H, Math.round(window.innerHeight - top - RESERVED_BELOW))));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  const counts = useMemo(() => {
    const c: Record<Quadrant, number> = { 'Quick Win': 0, 'Big Bet': 0, 'Incremental': 0, 'Deprioritize': 0 };
    points.forEach((p) => { c[p.quadrant] += 1; });
    return c;
  }, [points]);
  const list = useMemo(() => prioritized(points), [points]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = list.find((p) => p.id === selectedId) ?? list[0] ?? null;
  const selIdx = selected ? list.findIndex((p) => p.id === selected.id) : -1;
  const mx = sx(QUADRANT_MIDPOINT), my = sy(QUADRANT_MIDPOINT);

  const label = (q: Quadrant, x: number, y: number) => {
    const text = `${QUADRANT_LABEL[q]} · ${counts[q]}`;
    const w = text.length * 7.6 + 24;
    return (
      <g key={q}>
        <rect x={x - w / 2 + 2} y={y - 13} width={w} height={28} rx={3} fill="#000" opacity={0.08} />
        <rect x={x - w / 2} y={y - 15} width={w} height={28} rx={3} fill="#F5F5F5" stroke="#B9B9B9" />
        <text x={x} y={y + 4} textAnchor="middle" fontSize="14" fontWeight={600} fill="#333">{text}</text>
      </g>
    );
  };

  const preview = (() => {
    if (!selected) return null;
    const opp = getOpportunityById(selected.id);
    if (!opp) return null;
    const brief = getDecisionBrief(opp, selected.rank);
    const fig = hubFigures(selected.id);
    const first = brief.decisionText.replace(/^\s*Recommendation\s*[:\-–]\s*/i, '').split(/(?<=[.!?])\s/)[0];
    const inPortfolio = portfolioIds.has(selected.id);
    return { brief, fig, first, inPortfolio };
  })();

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-3 items-start">
      <div className="bg-white rounded-xl border border-[#e5e7eb] px-3 py-2 min-w-0">
        <div className="flex items-center gap-4 flex-nowrap whitespace-nowrap text-[10px] text-[#717182] px-1 pb-1 min-w-0">
          {headerSlot}
          <span className="ml-auto truncate" data-testid="matrix-hint">Click a point to preview · double-click to open · split at Medium (50)</span>
        </div>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ height: chartH }} className="w-full block"
          role="img" aria-label="Priority matrix: financial value by ease of implementation">
          {/* grid */}
          {Array.from({ length: 21 }, (_, i) => i * 5).map((v) => (
            <g key={v}>
              <line x1={sx(v)} y1={Y0} x2={sx(v)} y2={Y1} stroke="#E3E3E3" strokeWidth={1} />
              <line x1={X0} y1={sy(v)} x2={X1} y2={sy(v)} stroke="#E3E3E3" strokeWidth={1} />
            </g>
          ))}
          {/* crosshair */}
          <line x1={mx} y1={Y0 - 22} x2={mx} y2={Y1 - 20} stroke={AXIS} strokeWidth={4} />
          <line x1={X0} y1={my} x2={X1 + 4} y2={my} stroke={AXIS} strokeWidth={4} />
          {/* quadrant labels (top row above the plot, bottom row beside the Medium tick) */}
          {label('Big Bet', (X0 + mx) / 2, 26)}
          {label('Quick Win', (mx + X1) / 2, 26)}
          {label('Deprioritize', (X0 + mx) / 2, Y1 + 30)}
          {label('Incremental', (mx + X1) / 2, Y1 + 30)}
          {/* y axis */}
          <text transform={`translate(30 ${(Y0 + Y1) / 2}) rotate(-90)`} textAnchor="middle" fontSize="20" fontWeight={600} fill={AXIS}>Financial Value</text>
          {([['High', 100], ['Medium', 50], ['Low', 0]] as const).map(([t, v]) => (
            <text key={t} transform={`translate(70 ${sy(v) + (v === 100 ? 20 : v === 0 ? -4 : 0)}) rotate(-90)`} textAnchor="middle" fontSize="13" fill={AXIS}>{t}</text>
          ))}
          {/* x axis: Medium sits between the bottom labels; Low, title and High on the last line */}
          <text x={mx} y={Y1 + 35} textAnchor="middle" fontSize="13" fill={AXIS}>Medium</text>
          <text x={sx(0)} y={H - 10} textAnchor="start" fontSize="13" fill={AXIS}>Low</text>
          <text x={(X0 + X1) / 2} y={H - 8} textAnchor="middle" fontSize="19" fontWeight={600} fill={AXIS}>Ease of Implementation</text>
          <text x={sx(100)} y={H - 10} textAnchor="end" fontSize="13" fill={AXIS}>High</text>
          {/* points */}
          {[...points].sort((a, b) => Number(a.scored) - Number(b.scored)).map((p) => (
            <g key={p.id} data-matrix-point={p.id} className="cursor-pointer" onClick={() => setSelectedId(p.id)} onDoubleClick={() => onOpen(p.id)}
              onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover((h) => (h?.id === p.id ? null : h))}>
              {selected?.id === p.id && <circle cx={sx(p.ease)} cy={sy(p.financial)} r={15} fill="#FF6E4C" opacity={0.18} stroke="#FF6E4C" strokeWidth={2} />}
              {portfolioIds.has(p.id) && <circle cx={sx(p.ease)} cy={sy(p.financial)} r={10} fill="none" stroke="#18365E" strokeWidth={2} />}
              <circle cx={sx(p.ease)} cy={sy(p.financial)} r={hover?.id === p.id ? 8.5 : 6.5}
                fill={DOT} stroke="#fff" strokeWidth={1.5} />
            </g>
          ))}
          {/* tooltip drawn in SVG space so it stays aligned at any rendered size */}
          {hover && (() => {
            const tw = 330, th = 62;
            const px = sx(hover.ease), py = sy(hover.financial);
            const tx = Math.min(Math.max(px - tw / 2, X0), X1 - tw);
            const ty = py - th - 14 < 4 ? py + 14 : py - th - 14;
            const title = `#${hover.rank} ${hover.title}`;
            return (
              <g pointerEvents="none">
                <rect x={tx} y={ty} width={tw} height={th} rx={8} fill="#18365E" />
                <text x={tx + 12} y={ty + 20} fontSize="13" fontWeight={600} fill="#fff">{title.length > 44 ? `${title.slice(0, 43)}…` : title}</text>
                <text x={tx + 12} y={ty + 37} fontSize="12" fill="#ffffffcc">VRI {hover.vri.toFixed(1)} · Financial {Math.round(hover.financial)} · Ease {Math.round(hover.ease)}</text>
                <text x={tx + 12} y={ty + 53} fontSize="12" fill="#ffffffcc">{QUADRANT_LABEL[hover.quadrant]}{portfolioIds.has(hover.id) ? ' · in your portfolio' : ''}</text>
              </g>
            );
          })()}
          {points.length === 0 && (
            <text x={(X0 + X1) / 2} y={(Y0 + Y1) / 2 - 30} textAnchor="middle" fontSize="15" fill="#717182">
              No use cases match the current filters.
            </text>
          )}
        </svg>
      </div>

      {/* Preview */}
      <aside className="bg-white rounded-xl border border-[#e5e7eb] flex flex-col overflow-hidden" style={{ maxHeight: chartH + 34 }} aria-label="Use case preview">
        {selected && preview ? (
          <>
            <div className="px-4 pt-3 pb-2 border-b border-[#f1f5f9] flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#717182] uppercase tracking-wide">#{selIdx + 1} of {list.length} by VRI</span>
              <div className="ml-auto flex gap-1">
                <button type="button" aria-label="Previous by VRI" disabled={selIdx <= 0} onClick={() => setSelectedId(list[selIdx - 1].id)}
                  className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E]"><ChevronLeft className="w-4 h-4" /></button>
                <button type="button" aria-label="Next by VRI" disabled={selIdx >= list.length - 1} onClick={() => setSelectedId(list[selIdx + 1].id)}
                  className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E]"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="px-4 py-3 overflow-y-auto flex-1 space-y-3">
              <div>
                <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">{selected.category}</p>
                <h3 className="text-base font-bold text-[#18365E] leading-snug mt-0.5">{selected.title}</h3>
                <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                  <VerdictPill verdict={preview.brief.verdict} />
                  <QuadrantChip q={selected.quadrant} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {([['VRI', selected.vri.toFixed(1)], ['Financial', String(Math.round(selected.financial))], ['Ease', String(Math.round(selected.ease))]] as const).map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-[#F5F7FA] px-2.5 py-2 text-center">
                    <p className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">{k}</p>
                    <p className="text-base font-bold text-[#18365E] tabular-nums">{v}</p>
                  </div>
                ))}
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                {([
                  [<TrendingUp key="a" className="w-3.5 h-3.5" />, 'Annual value', fig(preview.fig.annualBenefitRange)],
                  [<Clock key="b" className="w-3.5 h-3.5" />, 'Payback', preview.fig.payback],
                  [<DollarSign key="c" className="w-3.5 h-3.5" />, 'Net 3-year value', preview.fig.netValue3Yr],
                  [<Calendar key="d" className="w-3.5 h-3.5" />, 'Time to value', preview.brief.ttv],
                ] as const).map(([icon, k, v]) => (
                  <div key={k as string}>
                    <dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide flex items-center gap-1">{icon}{k}</dt>
                    <dd className="font-bold text-[#18365E] mt-0.5">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-[#4b5563] leading-relaxed">{preview.first}</p>
              <p className="text-[10px] text-[#717182]">Financial figures: Base Case scenario.</p>
            </div>
            <div className="px-4 py-3 border-t border-[#f1f5f9] flex gap-2">
              {onTogglePortfolio && (
                <button type="button" onClick={() => onTogglePortfolio(selected.id)}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold ${preview.inPortfolio ? 'bg-[#FF6E4C] text-white' : 'bg-[#F5F7FA] text-[#18365E] border border-[#e5e7eb] hover:border-[#18365E]'}`}>
                  {preview.inPortfolio ? <><Check className="w-3.5 h-3.5" />In portfolio</> : <><Plus className="w-3.5 h-3.5" />Add to portfolio</>}
                </button>
              )}
              <button type="button" onClick={() => onOpen(selected.id)}
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-[#18365E] text-white hover:bg-[#122a4a]">
                Open full use case <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        ) : (
          <p className="p-4 text-xs text-[#717182]">No use cases match the current filters.</p>
        )}
      </aside>
    </div>
  );
};

export default PriorityMatrix;
